// El peso y las medidas del PAQUETE con los que nace un producto del lanzamiento cuando viene del
// catálogo (ADR-2026-10-18-la-importacion-de-tiendanube-trae-los-datos). Puro.
//
// Las medidas del producto (`productos.medidas`) y las del paquete (`productos.medidas_envio`) son
// cosas distintas y se guardan aparte: una mesa que viaja desarmada va en una caja chata. Regla:
//   1. Si el catálogo tiene el paquete (lo trajo la importación de Tiendanube), va ése.
//   2. Si no, y el producto se entrega ARMADO (no se desarma ni se pliega), el paquete se toma del
//      producto: la diferencia con la caja es chica y para cotizar el envío no cambia nada. Se
//      redondea hacia arriba, al centímetro y a los 100 g: una caja nunca es más chica que lo que lleva.
//   3. Si se entrega desarmado o plegado, o no se sabe, queda vacío y lo completa la persona (o la
//      ficha con IA), como hasta ahora.
import { normalizarMedidas, type UnidadMedida } from '../../lib/medidas';

export interface PaqueteInicial {
  peso: number | null;
  alto: number | null;
  ancho: number | null;
  profundidad: number | null;
}

export const PAQUETE_VACIO: PaqueteInicial = { peso: null, alto: null, ancho: null, profundidad: null };
const A_CM: Record<UnidadMedida, number> = { mm: 0.1, cm: 1, m: 100 };

const positivo = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Hacia arriba, sin arrastrar el error de coma flotante (75.0000001 → 75, no 76). */
const haciaArriba = (n: number, paso: number): number => Math.ceil(Math.round((n / paso) * 1000) / 1000) * paso;

export function paqueteInicialDesdeCatalogo(medidasRaw: unknown, envioRaw: unknown): PaqueteInicial {
  const envio = envioRaw && typeof envioRaw === 'object' ? (envioRaw as Record<string, unknown>) : {};
  const delEnvio: PaqueteInicial = {
    peso: positivo(envio.peso_kg),
    alto: positivo(envio.alto),
    ancho: positivo(envio.ancho),
    profundidad: positivo(envio.profundidad),
  };
  if (delEnvio.alto !== null && delEnvio.ancho !== null && delEnvio.profundidad !== null) return delEnvio;
  if (envio.se_entrega !== 'armado') return delEnvio;

  const m = normalizarMedidas(medidasRaw);
  if (!m || !m.alto || !m.ancho || !m.profundidad) return delEnvio;
  const cm = (v: number) => haciaArriba(v * A_CM[m.unidad], 1);
  const peso = delEnvio.peso ?? (m.peso_kg ? Math.round(haciaArriba(m.peso_kg, 0.1) * 10) / 10 : null);
  return { peso, alto: cm(m.alto), ancho: cm(m.ancho), profundidad: cm(m.profundidad) };
}

/**
 * Cómo queda `productos.medidas_envio` al guardar un paquete encima del que había. Un paquete que
 * confirmó la persona (`origen: 'usuario'`) no se pisa sin `sobrescribir`; y si se pisa con su
 * aprobación sigue confirmado, aunque lo escriba el agente: si no, lo que no cambió perdía la
 * protección (ADR-2026-10-19-revision-greptile-pdf-y-catalogo-2). Puro.
 */
export function paqueteSobreElGuardado(
  actual: Record<string, unknown>,
  paquete: Partial<Record<'alto' | 'ancho' | 'profundidad' | 'peso_kg', number | null>>,
  opts: { sobrescribir?: boolean; origen?: 'usuario' | 'ia' },
): { confirmado: true } | { medidas_envio: Record<string, unknown> } {
  const tieneValores = ['alto', 'ancho', 'profundidad', 'peso_kg'].some((k) => positivo(actual[k]) !== null);
  const confirmado = actual.origen === 'usuario' && tieneValores;
  if (confirmado && !opts.sobrescribir) return { confirmado: true };
  return { medidas_envio: { ...actual, ...paquete, unidad: 'cm', origen: confirmado ? 'usuario' : opts.origen ?? 'usuario' } };
}
